/**
 * App configuration.
 *
 * Every value in here comes from an `EXPO_PUBLIC_*` env var that is
 * baked into the JS bundle at build time:
 *
 *   • Native  — babel.config.js loads `.env.<APP_ENV>` via dotenv and
 *               then inlines `process.env.EXPO_PUBLIC_*` references
 *               using babel-plugin-transform-inline-environment-variables.
 *   • Web     — vite.config.ts does the same via `loadEnv(mode, …)`
 *               and the Vite `define` table, keyed on `--mode`.
 *
 * Which `.env` file wins is decided by build mode:
 *
 *   APP_ENV=development   (default for `npm run start/android/ios`)
 *     → .env.development  → api.rinsepay.design, pk_test_*
 *   APP_ENV=production    (set explicitly for release builds / CI)
 *     → .env.production   → api.rinsepay.app,    pk_live_*
 *
 * There are NO fallback URLs in this file anymore.  A misconfigured
 * build that forgets to export `APP_ENV` / pass `--mode` will throw on
 * module load, which is loud and obvious, rather than silently
 * defaulting to production and shipping a debug build that hits the
 * live API.  See docs/deployment-pipeline.md for the workflow.
 *
 * IMPLEMENTATION NOTE: the values below are written as LITERAL
 * `process.env.EXPO_PUBLIC_X` expressions rather than
 * `process.env[key]` dynamic lookups.  That's load-bearing — Vite's
 * `define` substitution and babel-plugin-transform-inline-environment-variables
 * both only rewrite literal member accesses.  Do not refactor this to
 * a loop/map; the literals are the whole point.
 */

function required(label: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `[config] ${label} is not set. ` +
        'Check that you have `.env.development` (or `.env.production`) at ' +
        'the app root and that APP_ENV is exported before building — e.g. ' +
        '`APP_ENV=development npm run android`.',
    );
  }
  return value;
}

export const config = {
  apiUrl: required('EXPO_PUBLIC_API_URL', process.env.EXPO_PUBLIC_API_URL),
  appUrl: required('EXPO_PUBLIC_APP_URL', process.env.EXPO_PUBLIC_APP_URL),
  qrBaseUrl: required(
    'EXPO_PUBLIC_QR_BASE_URL',
    process.env.EXPO_PUBLIC_QR_BASE_URL,
  ),
  // Stripe is allowed to be blank in dev (non-payment screens still
  // need to mount) — warn instead of throwing.  If you crash inside
  // Stripe screens, add a pk_test_* value to `.env.development`.
  stripePublishableKey: (() => {
    const key = process.env.EXPO_PUBLIC_STRIPE_PK;
    if (!key) {
      // eslint-disable-next-line no-console
      console.warn(
        '[config] EXPO_PUBLIC_STRIPE_PK not set — Stripe features will not work',
      );
      return '';
    }
    return key;
  })(),
} as const;
